import {
    DonutChart,
    GaugeChart,
    HexColor,
    LayoutCarousel,
    ProgressPolygonList,
    SeverityLevel,
    TableColumn
} from '@silverfort/sf-components';
import moment from 'moment';
import { EntitiesStats, InventoryEntity } from './types';
import { InsightsStatistics } from '../../modules/ispm/api/types/Insights';
import { AD_ENTITY_TYPES, RISK_LEVELS } from '@silverfort-nodelibs/common/utils/consts';
import { DonutChartData } from '@silverfort/sf-components/dist/Charts/DonutChart/types';
import { RISK_RULES } from '@silverfort-nodelibs/common/utils/insight';
import { PricingModulesStatus } from '../../context/contexts/PricingModuleContext';

export const INVENTORY_AD_TABS = [
    { value: 0, name: 'Users' },
    { value: 1, name: 'Resources' },
    { value: 2, name: 'Organizational Unit' },
    { value: 3, name: 'Groups' }
];

export const INVENTORY_TAB_TO_URL = {
    [INVENTORY_AD_TABS[0].value]: 'users',
    [INVENTORY_AD_TABS[1].value]: 'resources',
    [INVENTORY_AD_TABS[2].value]: 'ous',
    [INVENTORY_AD_TABS[3].value]: 'groups'
};

export const INVENTORY_TAB_TO_AD_TYPE = {
    [INVENTORY_AD_TABS[0].value]: AD_ENTITY_TYPES.USER,
    [INVENTORY_AD_TABS[1].value]: AD_ENTITY_TYPES.SERVER,
    [INVENTORY_AD_TABS[2].value]: AD_ENTITY_TYPES.OU,
    [INVENTORY_AD_TABS[3].value]: AD_ENTITY_TYPES.GROUP
};

const CHART_COLORS: HexColor[] = ['#FFAF39', '#FBDF40', '#50017A', '#FF115B', '#1C89AC'];

const calcTotalCount = (
    statArray?: {
        [key: string | number]: string | number;
    }[]
) => (statArray || []).reduce((sum, stat) => sum + +stat.count, 0);

const baseColumnsMap: { [key: string]: TableColumn<InventoryEntity> } = {
    name: {
        columnKey: 'name',
        title: `Name`
    },
    domain: {
        columnKey: 'domain_name',
        title: 'Domain',
        disableSort: true
    },
    creationDate: {
        columnKey: 'creation_date',
        title: 'Creation date',
        render: ({ creation_date }) => moment(creation_date).format('MMM DD, YYYY')
    },
    updateDate: {
        columnKey: 'update_date',
        title: 'Update date',
        disableSort: true,
        render: ({ update_date }) => moment(update_date).format('MMM DD, YYYY')
    },
    path: { columnKey: 'path', title: 'Path', disableSort: true }
};

const usersColumns: TableColumn<InventoryEntity>[] = [
    baseColumnsMap.name,
    {
        columnKey: 'identifier',
        title: 'User name'
    },
    baseColumnsMap.domain,
    {
        columnKey: 'risk_level',
        title: 'Risk level',
        render: ({ risk_level }) => (
            <div className="w-9">
                <SeverityLevel severity={risk_level || 1} />
            </div>
        )
    }
];

const groupsColumns: TableColumn<InventoryEntity>[] = [
    baseColumnsMap.name,
    baseColumnsMap.domain,
    baseColumnsMap.path,
    { columnKey: 'sid', title: 'SID', disableSort: true }
];

const resourceColumns: TableColumn<InventoryEntity>[] = [
    baseColumnsMap.name,
    baseColumnsMap.domain,
    { columnKey: 'operating_system', title: 'Operating system', disableSort: true },
    baseColumnsMap.path
];

const ousColumns: TableColumn<InventoryEntity>[] = [baseColumnsMap.name, baseColumnsMap.domain, baseColumnsMap.path];

const TAB_TO_COLUMNS_MAP = {
    [INVENTORY_AD_TABS[0].value]: usersColumns,
    [INVENTORY_AD_TABS[1].value]: resourceColumns,
    [INVENTORY_AD_TABS[2].value]: ousColumns,
    [INVENTORY_AD_TABS[3].value]: groupsColumns
};

const filterColumnsByModule = (
    columns: TableColumn<InventoryEntity>[],
    pricingModulesState: PricingModulesStatus | null
) => {
    return columns.filter((column) => {
        if (column.columnKey === 'risk_level') {
            return pricingModulesState?.ispm.onPremEnabled || pricingModulesState?.itdr.onPremEnabled;
        } else {
            return true;
        }
    });
};

const calcProgressBarVal = (value, baseline) => {
    if (baseline) {
        return 100 * (value / baseline);
    }

    return 0;
};

export const buildTableColumns = (
    selectedTab: number,
    pricingModulesState: PricingModulesStatus | null
): TableColumn<InventoryEntity>[] => {
    const columns = TAB_TO_COLUMNS_MAP[selectedTab];

    if (columns) {
        return filterColumnsByModule(columns, pricingModulesState);
    }

    console.error('Unknown inventory tab.');
    return [];
};

const calcResourceOsData = (entitiesStats: EntitiesStats | undefined) => {
    const osStats = entitiesStats?.resources?.operating_system;

    if (!osStats) {
        console.error('Missing resources operating system statistics.');
        return;
    }

    const chartData: DonutChartData[] = [];

    let colorIndex = 0;

    for (const osCounter of osStats) {
        chartData.push({
            name: osCounter.operating_system as string,
            value: +osCounter.count,
            fill: CHART_COLORS[colorIndex || 0]
        });

        colorIndex++;
    }

    return chartData;
};

const calcEntityDomainData = (entitiesStats: EntitiesStats, onItemClick, type) => {
    if (!entitiesStats[type]) {
        console.error('unknown entity type ' + type);
    }

    const domainStats = entitiesStats[type]?.domain_name;

    if (!domainStats) {
        console.error(`Missing ${type} domains statistics.`);
        return;
    }

    const chartData: DonutChartData[] = [];

    let colorIndex = 0;

    for (const domainCounter of domainStats) {
        chartData.push({
            name: domainCounter.domain_name as string,
            value: +domainCounter.count,
            fill: CHART_COLORS[colorIndex || 0],
            onClick: () => onItemClick('domain_name', domainCounter.domain_name)
        });

        colorIndex++;
    }

    return chartData;
};

const calcRiskData = (data: InsightsStatistics, onItemClick) => {
    if (!data.riskStatisticsByUser) {
        console.error('Missing risk statistics.');
        return;
    }

    return [
        {
            name: 'Critical',
            value: data.riskStatisticsByUser[RISK_LEVELS.CRITICAL.value],
            fill: '#C5192F',
            onClick: () => onItemClick('risk_level', RISK_LEVELS.CRITICAL.value)
        },
        {
            name: 'High',
            value: data.riskStatisticsByUser[RISK_LEVELS.HIGH.value],
            fill: '#F5445A',
            onClick: () => onItemClick('risk_level', RISK_LEVELS.HIGH.value)
        },
        {
            name: 'Medium',
            value: data.riskStatisticsByUser[RISK_LEVELS.MEDIUM.value],
            fill: '#FC8154',
            onClick: () => onItemClick('risk_level', RISK_LEVELS.MEDIUM.value)
        },
        {
            name: 'Low',
            value: data.riskStatisticsByUser[RISK_LEVELS.LOW.value],
            fill: '#F7B748',
            onClick: () => onItemClick('risk_level', RISK_LEVELS.LOW.value)
        }
    ];
};

const calcUserByTypeData = (data: InsightsStatistics) => {
    if (!data?.insightsStatistics || !data?.systemStatistics) {
        console.error('Missing system and insights statistics.');
        return;
    }

    const counters = {
        domainAdmin:
            data.systemStatistics[RISK_RULES.DOMAIN_ADMIN] || data.insightsStatistics[RISK_RULES.DOMAIN_ADMIN] || 0,
        privilegedUsers:
            data.systemStatistics[RISK_RULES.PRIVILEGED_USER] ||
            data.insightsStatistics[RISK_RULES.PRIVILEGED_USER] ||
            0,
        globalAdmins:
            data.systemStatistics[RISK_RULES.ENTRA_ID_ADMIN] || data.insightsStatistics[RISK_RULES.ENTRA_ID_ADMIN] || 0,
        hybrid: data.systemStatistics['synced_users'] || 0
    };

    const sortedValuesDesc = Object.values(counters).sort((a, b) => b - a);
    const baselineValue = sortedValuesDesc[0];

    const userByDataArray = [
        {
            label: 'Domain Admin',
            value: counters.domainAdmin,
            progress: calcProgressBarVal(counters.domainAdmin, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        },
        {
            label: 'Hybrid Accounts',
            value: counters.hybrid,
            progress: calcProgressBarVal(counters.hybrid, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        },
        {
            label: 'Privileged Users',
            value: counters.privilegedUsers,
            progress: calcProgressBarVal(counters.privilegedUsers, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        },
        {
            label: 'Global Admins',
            value: counters.globalAdmins,
            progress: calcProgressBarVal(counters.globalAdmins, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        }
    ];

    return userByDataArray.sort((a, b) => b.value - a.value);
};

const calcGroupsTopData = (entitiesStats: EntitiesStats | undefined) => {
    const topGroupStats = entitiesStats?.groups?.topGroup;

    if (!topGroupStats) {
        console.error('Missing groups top members statistics.');
        return;
    }

    const baselineValue = topGroupStats[0]?.count || 0;

    const topGroupsDataArray = topGroupStats.map((stat) => {
        return {
            label: stat.topGroup as string,
            value: stat.count as number,
            progress: calcProgressBarVal(stat.count, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        };
    });

    return topGroupsDataArray;
};

const calcOuTopData = (entitiesStats: EntitiesStats | undefined) => {
    const topOusStats = entitiesStats?.ous?.topOu;

    if (!topOusStats) {
        console.error('Missing ous top members statistics.');
        return;
    }

    const baselineValue = topOusStats[0]?.count || 0;

    const topOusDataArray = topOusStats.map((stat) => {
        return {
            label: stat.topOu as string,
            value: stat.count as number,
            progress: calcProgressBarVal(stat.count, baselineValue),
            endColor: '#D6C7DD',
            startColor: '#50017A'
        };
    });

    return topOusDataArray;
};

const getGroupsWidgets = (entitiesStats: EntitiesStats | undefined, systemStats: InsightsStatistics, onItemClick) => {
    const topGroupsChartData = calcGroupsTopData(entitiesStats);
    const domainChartData = entitiesStats ? calcEntityDomainData(entitiesStats, onItemClick, 'groups') : [];
    const groupsCount = systemStats.systemStatistics?.groups;
    const protectedCount = entitiesStats?.groups.protectedGroup[0].count as number;
    const protectedPercents = groupsCount && protectedCount ? (protectedCount / groupsCount) * 100 : 0;
    return (
        <>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    contentClassName="text-13"
                    title="Group by Domain"
                    pages={[
                        <DonutChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.groups?.domain_name}
                            data={domainChartData || []}
                            totalText={calcTotalCount(entitiesStats?.groups?.domain_name)}
                            isMappingVisible
                        />
                    ]}
                />
            </div>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Top Groups by Members"
                    pages={[
                        <ProgressPolygonList
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.groups?.topGroup}
                            items={topGroupsChartData || []}
                        />
                    ]}
                />
            </div>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Protected by Policy"
                    pages={[
                        <GaugeChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.groups?.protectedGroup}
                            label={`${protectedCount}/${groupsCount}`}
                            value={protectedPercents}
                            startColor="#FF115B"
                            endColor="#FFD2E0"
                        />
                    ]}
                />
            </div>
        </>
    );
};

const getOusWidgets = (systemStats: InsightsStatistics, entitiesStats: EntitiesStats | undefined, onItemClick) => {
    const topOuChartData = calcOuTopData(entitiesStats);
    const domainChartData = entitiesStats ? calcEntityDomainData(entitiesStats, onItemClick, 'ous') : [];
    const ouCount = systemStats.systemStatistics?.ous;
    const protectedCount = entitiesStats?.ous?.protectedOu[0].count as number;
    const protectedPercents = ouCount && protectedCount ? (protectedCount / ouCount) * 100 : 0;
    return (
        <>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Org Unit by Domain"
                    contentClassName="text-13"
                    pages={[
                        <DonutChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.ous?.domain_name}
                            data={domainChartData || []}
                            totalText={calcTotalCount(entitiesStats?.ous?.domain_name)}
                            isMappingVisible
                        />
                    ]}
                />
            </div>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Org Unit by Members"
                    pages={[
                        <ProgressPolygonList
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.ous?.topOu}
                            items={topOuChartData || []}
                        />
                    ]}
                />
            </div>
            <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Protected by Policy"
                    pages={[
                        <GaugeChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.ous?.protectedOu}
                            label={`${protectedCount}/${ouCount}`}
                            value={protectedPercents}
                            startColor="#FF115B"
                            endColor="#FFD2E0"
                        />
                    ]}
                />
            </div>
        </>
    );
};

const getResourcesWidgets = (entitiesStats: EntitiesStats | undefined, onItemClick) => {
    const osChartData = calcResourceOsData(entitiesStats);
    const domainChartData = entitiesStats ? calcEntityDomainData(entitiesStats, onItemClick, 'resources') : [];

    return (
        <>
            <div className="h-50 w-1/2 min-w-[350px] border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Resource by Operating System"
                    contentClassName="text-13"
                    pages={[
                        <DonutChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.resources}
                            data={osChartData || []}
                            totalText={calcTotalCount(entitiesStats?.resources?.operating_system)}
                            isMappingVisible
                        />
                    ]}
                />
            </div>
            <div className="h-50 w-1/2 min-w-[350px] border border-sf-gray-100 rounded-lg">
                <LayoutCarousel
                    title="Resource by Domain"
                    contentClassName="text-13"
                    pages={[
                        <DonutChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.resources}
                            data={domainChartData || []}
                            totalText={calcTotalCount(entitiesStats?.resources?.domain_name)}
                            isMappingVisible
                        />
                    ]}
                />
            </div>
        </>
    );
};

const getUsersWidgets = (
    systemStats: InsightsStatistics,
    entitiesStats: EntitiesStats | undefined,
    onItemClick,
    pricingModulesState: PricingModulesStatus | null
) => {
    const usersByTypeData = systemStats.systemStatistics ? calcUserByTypeData(systemStats) : [];
    const usersByDomainData = entitiesStats ? calcEntityDomainData(entitiesStats, onItemClick, 'users') : [];
    let riskChartData;

    if (pricingModulesState?.ispm?.onPremEnabled || pricingModulesState?.itdr?.onPremEnabled) {
        riskChartData = systemStats.riskStatisticsByUser ? calcRiskData(systemStats, onItemClick) : [];
    }

    return (
        <>
            {riskChartData && (
                <div className="h-50 w-1/3 border border-sf-gray-100 rounded-lg">
                    <LayoutCarousel
                        title="Risk Level"
                        contentClassName="text-13"
                        pages={[
                            <DonutChart
                                isAnimationActive={false}
                                isLoading={!systemStats.riskStatisticsByUser}
                                data={riskChartData || []}
                                totalText={Object.values(systemStats?.riskStatisticsByUser || {}).reduce(
                                    (counter, stat) => counter + stat,
                                    0
                                )}
                                isMappingVisible
                            />
                        ]}
                    />
                </div>
            )}
            <div className={`h-50 ${riskChartData ? 'w-1/3' : 'w-1/2'} border border-sf-gray-100 rounded-lg`}>
                <LayoutCarousel
                    title="Users by Domain"
                    contentClassName="text-13"
                    pages={[
                        <DonutChart
                            isAnimationActive={false}
                            isLoading={!entitiesStats?.users?.domain_name}
                            data={usersByDomainData || []}
                            totalText={calcTotalCount(entitiesStats?.users?.domain_name)}
                            isMappingVisible
                        />
                    ]}
                />
            </div>
            <div className={`h-50 ${riskChartData ? 'w-1/3' : 'w-1/2'} border border-sf-gray-100 rounded-lg`}>
                <LayoutCarousel
                    contentClassName="max-h-[166px] pt-[0]"
                    title="Users by Type"
                    pages={[
                        <ProgressPolygonList
                            isAnimationActive={false}
                            isLoading={!systemStats.systemStatistics}
                            items={usersByTypeData?.slice(0, 3) || []}
                        />,
                        <ProgressPolygonList
                            isAnimationActive={false}
                            isLoading={!systemStats.systemStatistics}
                            items={usersByTypeData?.slice(3) || []}
                        />
                    ]}
                />
            </div>
        </>
    );
};

export const getWidgetsByTab = (
    selectedTab: number,
    systemStats: InsightsStatistics,
    entitiesStats: EntitiesStats | undefined,
    onItemClick: (filterId: string, filterValue: string | number) => void,
    pricingModulesState: PricingModulesStatus | null
) => {
    switch (selectedTab) {
        case INVENTORY_AD_TABS[0].value: {
            return getUsersWidgets(systemStats, entitiesStats, onItemClick, pricingModulesState);
        }
        case INVENTORY_AD_TABS[1].value: {
            return getResourcesWidgets(entitiesStats, onItemClick);
        }
        case INVENTORY_AD_TABS[2].value: {
            return getOusWidgets(systemStats, entitiesStats, onItemClick);
        }
        case INVENTORY_AD_TABS[3].value: {
            return getGroupsWidgets(entitiesStats, systemStats, onItemClick);
        }
        default:
            break;
    }
};

export const getStatsCountByTab = (selectedTab: number, systemStats: InsightsStatistics | undefined) => {
    if (!systemStats?.systemStatistics) {
        return 0;
    }

    switch (selectedTab) {
        case INVENTORY_AD_TABS[0].value:
            return Object.values(systemStats.riskStatisticsByUser || []).reduce((counter, data) => counter + data, 0);
        case INVENTORY_AD_TABS[1].value:
            return systemStats.systemStatistics.servers + systemStats.systemStatistics.hosts;
        case INVENTORY_AD_TABS[2].value:
            return systemStats.systemStatistics.ous;
        case INVENTORY_AD_TABS[3].value:
            return systemStats.systemStatistics.groups;
        default:
            return 0;
    }
};

// Map AD entity types to inventory tabs
export const getTabFromAdType = (adType: number): number => {
    switch (adType) {
        case AD_ENTITY_TYPES.USER:
            return 0;
        case AD_ENTITY_TYPES.HOST:
        case AD_ENTITY_TYPES.SERVER:
            return 1;
        case AD_ENTITY_TYPES.OU:
            return 2;
        case AD_ENTITY_TYPES.GROUP:
            return 3;
        default:
            return 0;
    }
};
